import { inject, Injectable, InjectionToken, Provider, Type } from '@angular/core';
import { UserService } from '@services/user.service';
import { FilterDef } from '@shared/dataview/core/dataview-types';
import { SelectListItem } from '@shared/models/select-list-item';
import { catchError, defaultIfEmpty, defer, Observable, of, shareReplay } from 'rxjs';

export interface FilterOptionsContext {
  accessibleTenants: Array<{ id: string; name: string }>;
  groupByTenant: boolean;
}

export interface FilterOptionsLoader {
  canLoad(def: FilterDef): boolean;
  load(def: FilterDef, context: FilterOptionsContext): Observable<SelectListItem[]>;
}

export const DATA_VIEW_FILTER_OPTIONS = new InjectionToken<readonly FilterOptionsLoader[]>('DATA_VIEW_FILTER_OPTIONS');

export function provideFilterOptionLoaders(...loaders: Array<Type<FilterOptionsLoader>>): Provider[] {
  return loaders.flatMap(loader => [loader, { provide: DATA_VIEW_FILTER_OPTIONS, useExisting: loader, multi: true }]);
}

export abstract class SourceFilterOptionsLoader implements FilterOptionsLoader {
  abstract readonly source: string;

  canLoad(def: FilterDef): boolean {
    return def.optionsSource === this.source;
  }

  abstract load(def: FilterDef, context: FilterOptionsContext): Observable<SelectListItem[]>;
}

@Injectable({ providedIn: 'root' })
export class DataViewFilterOptionsService {
  private readonly loaders = inject(DATA_VIEW_FILTER_OPTIONS, { optional: true }) ?? [];
  private readonly userService = inject(UserService);

  private readonly cache = new Map<string, Observable<SelectListItem[]>>();

  options(def: FilterDef): Observable<SelectListItem[]> {
    if (def.options?.length) return of(def.options);
    if (!def.optionsSource) return of([]);

    const cached = this.cache.get(def.id);
    if (cached) return cached;

    const loader = this.loaders.find(candidate => candidate.canLoad(def));
    if (!loader) {
      console.warn(`DataView: no options loader claims "${def.optionsSource}" for filter "${def.id}"`);
      return of([]);
    }

    const options = defer(() => loader.load(def, this.context())).pipe(
      defaultIfEmpty([] as SelectListItem[]),
      catchError(error => {
        console.error(`DataView: failed to load options for filter "${def.id}"`, error);
        return of([] as SelectListItem[]);
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    this.cache.set(def.id, options);
    return options;
  }

  clearCache(filterId?: string): void {
    if (filterId) this.cache.delete(filterId);
    else this.cache.clear();
  }

  private context(): FilterOptionsContext {
    const accessibleTenants = this.userService.getUserAccessibleTenants().map(tenant => ({ id: tenant.id, name: tenant.name }));
    return { accessibleTenants, groupByTenant: accessibleTenants.length > 1 };
  }
}

export function groupOptionsByTenant(options: SelectListItem[], tenantId: string, context: FilterOptionsContext): SelectListItem[] {
  if (!context.groupByTenant) return options;
  const tenantName = context.accessibleTenants.find(tenant => tenant.id === tenantId)?.name ?? tenantId;
  return options.map(option => ({ ...option, group: tenantName }));
}

export function groupByValue(tenantId: string, id: string): string {
  return `groupBy:${tenantId}|${id}`;
}

export function toOptions(values: string[]): SelectListItem[] {
  return values.map(value => ({ name: value, value }));
}
